/**
 * Lessons on the QParam stack, built on the recorded runs of the Pauli ladder
 * (backend/scripts/build_formalrv_explorer.py). Every number quoted here is
 * visible in the run the lesson opens. `code` spans use backticks.
 */

export type LessonPane = 'qppm' | 'qec' | 'sysprog' | 'passes' | 'sysspec' | 'machine' | 'diff'

export interface Lesson {
  id: string
  title: string
  example: string
  variant?: string
  pane: LessonPane
  summary: string
  body: string[]
  tryIt: { variant: string | null; label: string; note: string; pane?: LessonPane }[]
  check: { q: string; a: string }[]
}

export const LESSONS: Lesson[] = [
  {
    id: 'one-gate',
    title: 'One X gate, four languages',
    example: 'pauli',
    pane: 'qppm',
    summary: 'Follow the smallest program through every stage to a resource verdict.',
    body: [
      'A QParam program states an algorithm and its specification together. `program[q: n]: X(0)` applies one X gate to wire 0 of a register `q` of `n` wires.',
      'Its `spec` has binders and five columns. `∀ v < 2` ranges over the input bit; `requires 0 < n ∧ n ≤ dim` constrains the parameters; `assumes {{ |v⟩_q[0] }}` and `ensures {{ |1 ⊕ v⟩_q[0] }}` are the states before and after; `outcomes none` says nothing is measured; `resource` counts the gates: one Clifford.',
      'QParamPPM (`.qppm`) makes every measurement, magic state and frame update explicit. A Pauli gate needs no physical operation: it becomes `frame X q[0]`, a correction tracked classically in the Pauli frame. The specification is carried along, and its resource line now counts frames (`frame: 1`) instead of gates.',
      'QParamQEC (`.qec`) chooses a code. Each logical qubit is a distance-3 surface code, `Surface [[9,1,3]]`: 9 physical qubits, 1 logical qubit, distance 3, with every stabilizer written out. The logical X is `logX[0] X[0]X[3]X[6]`, and the frame update becomes `frame X[LogicData[0][0]]` on block 0.',
      'QParamSys (`.sysprog`) adds time, physical qubits and the machine. The one system call is `@0 ns pauli_frame_update X data[0], X data[3], X data[6]`: the three qubits of the logical X. The listing also carries contracts: the program\'s budget, and a maintenance contract for each code block (9 block qubits, 8 measure qubits, a 7,200 ns syndrome round).',
      'The resource VM reads the listing and the machine on its own and admits the schedule: 17 peak qubits (9 data qubits plus the 8 measure qubits that keep the block\'s syndrome extracted in the background) and 100 ns, the machine\'s `pauli_frame_update_ns`.',
    ],
    tryIt: [
      { variant: 'tight-budget', label: 'A budget of 9 qubits', pane: 'sysspec',
        note: 'Give the program only the 9 data qubits. The staged compiler refuses before any schedule exists: the data register and the 8 measure qubits of its maintained block do not fit. Click the qec_sys_staged arrow to read its C6 check.' },
      { variant: null, label: 'Back to the full run', pane: 'passes',
        note: 'Open Passes: the staged compiler\'s expand, system-call and allocation passes, one dump each.' },
    ],
    check: [
      { q: 'Why does the frame update touch data[0], data[3] and data[6]?',
        a: 'They are the support of the code\'s logical X operator, `logX[0] X[0]X[3]X[6]`. Applying X to all three applies the logical X.' },
      { q: 'The program has one gate. Why does the VM count 17 qubits?',
        a: '9 data qubits of the surface block plus 8 measure qubits that the maintenance contract reserves for syndrome extraction while the block is alive.' },
      { q: 'Which stage first mentions time?',
        a: 'The `.sysprog`: start times such as `@0 ns` and the machine\'s latencies. The stages above it are untimed.' },
    ],
  },
  {
    id: 'two-blocks',
    title: 'Two gates, two blocks',
    example: 'pauli-seq',
    pane: 'sysprog',
    summary: 'Sequencing, a second code block, and a machine that is too small.',
    body: [
      '`X(0)` then `X(1)`: the specification now binds two bits, `∀ v < 2 . ∀ w < 2`, and requires `1 < n`, so the two wires exist.',
      'At n = 2 the `.qec` holds two surface blocks and the `.sysprog` declares `data: 18`. Both frame updates start at `@0 ns`: they are classical bookkeeping on different blocks, so nothing forces one after the other. The VM reports 34 peak qubits (18 data + 16 measure) and 100 ns.',
      'Each maintained block also holds machine services in the background. The VM\'s table shows them against the machine: gate slots, decoders, classical and ancilla bandwidth.',
    ],
    tryIt: [
      { variant: 'own-machine', label: 'On PauliSeq.machine', pane: 'machine',
        note: 'The machine file shipped with the example has one parallel gate slot (`max_parallel_gates: 1 total`); maintaining two blocks needs two. The VM refuses: `max_parallel_gates: reservation 2 exceeds limit 1`. Click the qec_sys_staged arrow: the compiler\'s own C6 check had reported the schedule within every machine service. The two disagree, and the VM\'s refusal stands; this is why the checker is a separate program.' },
    ],
    check: [
      { q: 'Why do both frame updates start at 0 ns?',
        a: 'They act on different blocks and only update the classical Pauli frame, so the schedule has no dependence between them.' },
      { q: 'Where do the 34 qubits come from?',
        a: 'Two blocks, each with 9 data qubits and 8 measure qubits: 2 × (9 + 8).' },
    ],
  },
  {
    id: 'loops',
    title: 'Loops stay loops',
    example: 'pauli-loop',
    pane: 'sysprog',
    summary: 'Symbolic bounds and index expressions survive every stage.',
    body: [
      '`for 0 ≤ i < n: X(i)` flips every wire. The specification says the register goes from `v` to `v ⊕ (2^n - 1)`, and its resource column is symbolic: `clifford: n`.',
      'No compiler in the chain unrolls the loop. In the `.sysprog` it is still `for 0 <= qecSpecial0 < 2 every 100 ns:`, with operands such as `data[9*qecSpecial0 + 3]` written as expressions of the loop variable (9 physical qubits per block). This is how a paper-scale program stays a few thousand lines while standing for billions of operations.',
      '`every 100 ns` is the loop\'s period: its trips run one after another, so this run takes 200 ns, where the two separate statements of the previous lesson both started at 0 ns.',
    ],
    tryIt: [
      { variant: 'scale', label: 'n = 4', pane: 'sysprog',
        note: 'The same program at n = 4 on the same machine. The VM refuses: `max_parallel_gates: reservation 16 exceeds limit 8`. Four maintained blocks need 4 gate slots each, and the machine has 8.' },
      { variant: 'pauli-loop-bad-bound', label: 'PauliLoopBadBound.qppm', pane: 'diff',
        note: 'A deliberately wrong artifact that loops to `n-1`. The VM admits it, because fewer operations cost less (100 ns). The last lesson explains who catches it.' },
    ],
    check: [
      { q: 'How long would the `.sysprog` loop be at n = 1000?',
        a: 'The same lines: only the bound and the register size change. The loop is never unrolled.' },
      { q: 'Why 200 ns here but 100 ns for two separate gates?',
        a: 'The loop\'s trips are scheduled one per 100 ns period; the two independent statements of PauliSeq were both placed at 0 ns.' },
    ],
  },
  {
    id: 'conditions',
    title: 'A classical condition',
    example: 'pauli-cond',
    pane: 'qec',
    summary: 'How an `if` on a classical parameter is compiled, and why the VM bounds it.',
    body: [
      '`if (cst >> i) % 2 = 1: X(i)` applies X where bit i of the classical constant `cst` is set, so the register ends in `v ⊕ cst`. The resource column `clifford: popc(n, cst)` counts the set bits.',
      'This run uses cst = 2, binary 10: bit 0 is clear and bit 1 is set, so both branches are exercised. It is the point FormalRV\'s Lean files use as their witness for the same reason.',
      'Below the source there is no `if`. The condition becomes two loops that run zero or one times: `for 0 <= rv_g0 < 2/2^qecSpecial0%2` takes the `then` branch once when the bit is set, and `for 0 <= rv_e0 < 1 - 2/2^qecSpecial0%2` runs the empty `else` (`skip`) otherwise.',
      'The VM bounds guarded code as if every guard were taken (its scope says "all-taken guards"), so the runtime it reports, 200 ns, is an upper bound.',
    ],
    tryIt: [
      { variant: 'pauli-cond-bad-polarity', label: 'PauliCondBadPolarity.qppm', pane: 'diff',
        note: 'A wrong artifact that flips the condition. Same cost, wrong meaning: the VM admits it.' },
    ],
    check: [
      { q: 'With cst = 2, what does `2/2^i % 2` evaluate to at i = 0 and i = 1?',
        a: '`2/1 % 2 = 0` at i = 0, so no X on wire 0; `2/2 % 2 = 1` at i = 1, so one X on wire 1.' },
      { q: 'Why is the VM\'s runtime only an upper bound here?',
        a: 'The VM charges every guard as taken, so a branch that does not run is still counted.' },
    ],
  },
  {
    id: 'gadgets',
    title: 'Gadgets and calls',
    example: 'pauli-call',
    pane: 'sysprog',
    summary: 'Gadgets keep their own specifications, calls stay calls, and every gadget gets a resource contract.',
    body: [
      '`gadget loadConst[q: w](w, k)` packages the conditional loop with its own specification, `ensures {{ |v ⊕ k⟩_q[0, w) }}`. The program calls it once: `loadConst(n, cst) @ id()`, where `@ id()` maps the gadget\'s wires onto the caller\'s.',
      'Calls stay calls. The `.sysprog` holds `gadget loadConst[...] spans 200 ns:`, and the program is a single line, `@0 ns call loadConst(0, 0, 0, 0, 0, 0, 0, 0)`; the arguments are the base addresses of the data, ancilla, syndrome and record registers handed to the gadget.',
      'The staged compiler needs a resource contract for every gadget. Open Contracts: this explorer supplies `spec loadConst` with the program\'s qubit and time budget and no machine services, since frame updates are classical. The VM checks the call against that contract and the program against its own.',
      'The meaning of the gadget is proved separately: the example folder\'s Lean files prove the source specification. The resource VM never reads them.',
    ],
    tryIt: [
      { variant: null, label: 'Passes', pane: 'passes',
        note: 'The expand pass shows the gadget body with symbolic addresses, `data[(rv_database + qecSpecial1)*9 + 3]`: the caller\'s base address plus the block offset.' },
      { variant: 'pauli-call-bad-arg', label: 'PauliCallBadArg.qppm', pane: 'diff',
        note: 'A wrong artifact that passes the wrong argument to the gadget. The VM admits it.' },
    ],
    check: [
      { q: 'Why does the program\'s `.sysprog` have one line however large n is?',
        a: 'The call is kept as a call; the gadget body is compiled once, with symbolic base addresses and loop bounds.' },
      { q: 'Which tool checks that `loadConst` does what its spec says?',
        a: 'Lean, from the example\'s proof files. The compiler and the resource VM check structure and cost, not meaning.' },
    ],
  },
  {
    id: 'who-checks',
    title: 'Who catches what',
    example: 'pauli',
    variant: 'pauli-bad-axis',
    pane: 'diff',
    summary: 'Lean, the compiler\'s checks and the resource VM each see different mistakes.',
    body: [
      'Three independent checks guard the chain. Lean proves what programs mean: each source specification, and that every stored artifact is the compilation of its source. The staged compiler checks structure as it goes: code templates (C2), system calls (C4), allocation (C5), capacity and qubit budgets (C6). The resource VM recomputes peak qubits, services and runtime from the listing and the machine, and checks them against the contracts.',
      'The corpus ships deliberately wrong `.qppm` files. Each parses, and each still claims the specification of the program it corrupts. `PauliBadAxis.qppm` applies `frame Z` where the program needs `frame X`: the same cost and the wrong meaning, so the VM admits it with the same 17 qubits and 100 ns.',
      'What catches it is the Lean statement that the stored artifact equals the compilation of the source. The good file satisfies `pauli_artifact_is_the_compilation`, checked by `decide`; a wrong file cannot, because it is not what the compiler produces. The Diff tab shows exactly where it departs.',
      'Not every wrong file is invisible to cost checking. `PauliBadWire.qppm` addresses a wire outside the register, and the VM refuses it: `data operand exceeds program register bound`.',
    ],
    tryIt: [
      { variant: 'pauli-bad-axis', label: 'PauliBadAxis.qppm', pane: 'diff', note: 'Admitted by the VM: same cost, wrong meaning.' },
      { variant: 'pauli-bad-wire', label: 'PauliBadWire.qppm', pane: 'diff', note: 'Refused by the VM: an operand outside the register.' },
    ],
    check: [
      { q: 'A wrong artifact passes the resource VM. Does that make the VM\'s verdict worthless?',
        a: 'No. The VM answers a different question: does this schedule fit this machine and these contracts? Whether the program computes the right thing is Lean\'s question. The leaderboard reports both, stage by stage.' },
      { q: 'Which check refused the 9-qubit budget in the first lesson?',
        a: 'The staged compiler\'s C6 qubit check, before any schedule existed. The VM never ran.' },
    ],
  },
]
